import { APIRequestState } from 'constants/API'
import { EntityKeys, QueryType, queryClient } from 'utils/reactQuery'
import { FC, Fragment, ReactNode, useCallback, useEffect, useLayoutEffect, useMemo } from 'react'
import { ProductKind, ProductType } from 'constants/product'
import { bigFromDecimal, bigFromFee, decimalFromBig, formatPrice } from 'utils/price'
import { useUpsellPayment, useUpsellPurchaseConfiguration } from './contexts'

import BlockInfo from 'components/common/BlockInfo/BlockInfo'
import Button from 'components/common/Button/Button'
import { CheckCard } from 'components/common/ExpandableCard'
import { Color } from 'constants/assets'
import { CreditCardOutlined } from '@mui/icons-material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { MUIBadge } from 'components/common/MUIBadge'
import Modal from 'components/common/Modals/Modal/Modal'
import { PreferredPaymentMethodSwitch } from 'components/common/PreferredPaymentMethodSwitch'
import { QueryStatus } from 'components/common/QueryStatus'
import ReactLoading from 'react-loading'
import { StripePaymentWrapper } from '../StripePaymentWrapper'
import { Tag } from 'components/common/Tags'
import TransitionAppear from 'components/common/TransitionAppear/TransitionAppear'
import { Typography } from '@mui/material'
import { productKindIsPreferredPaymentMethod } from 'utils/typeguards'
import styles from './UpsellPayment.module.sass'
import { useActionPopup, useFormatterLanguage } from 'utils/hooks'
import { useAuth0 } from 'utils/auth'
import { useFeatureFlag } from 'utils/featureFlags'
import { useSnackbar } from '../../../../../contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

export const UpsellPaymentController: FC<{
  children?: ReactNode
}> = ({
  children
}) => {

    const { t, i18n } = useTranslation(['upsell_purchase_modal', 'product', 'product_kind', 'product_kind_disclaimer', 'badges'])
    const formatterLanguage = useFormatterLanguage()
    const { showConfirm } = useActionPopup()
    const { roles } = useAuth0()

    const {
      isOpen,
      productId,
      productType,
      productQuantity,
      total,
      successTitle,
      beforeConfirmActionState,
      beforeConfirmActionMutation,
      successNode,
      onSuccess,
      afterClosed,
      onClose,
      beforeConfirmAction,
      doStripRedirect,
    } = useUpsellPurchaseConfiguration()

    const {
      paymentIntent,
      isStripeUsed,
      selectedPaymentProduct,
      paymentProducts,
      isForeignPurchase,
      isPaymentElementValid,
      isStripeProcessing,
      stripeError,
      isStripeConfirmed,
      isPurchaseReady,
      updatePreferredPaymentMethod,
      isReceiptEmailValid,
      selectedSavedPaymentMethod,
      isPaymentLoading,
      setSelectedPaymentProduct,
      setIsStripeConfirmed,
      setStripeError,
      setDoStripeRedirect,
    } = useUpsellPayment()

    const { userCanPayByInvoice } = useUserData()
    const { spawnSuccessToast } = useSnackbar()

    const isSubmitProcessing = useMemo(() => beforeConfirmActionState === APIRequestState.RUNNING || (isStripeUsed && isStripeProcessing), [beforeConfirmActionState, isStripeProcessing, isStripeUsed])
    const isLoading = useMemo(() => !isPurchaseReady || isSubmitProcessing, [isPurchaseReady, isSubmitProcessing])
    const isPurchaseFinished = useMemo(() => !isStripeUsed && beforeConfirmActionState === APIRequestState.OK, [beforeConfirmActionState, isStripeUsed])

    const totalWithPayment = useMemo(() => {
      if (!total) return total

      const newTotal = { ...total }

      newTotal.value = decimalFromBig(bigFromDecimal(newTotal.value).plus(bigFromFee(selectedPaymentProduct?.feePrice ?? 0)))

      return newTotal
    }, [selectedPaymentProduct?.feePrice, total])

    const extraPrice = useMemo(() => {
      if (!selectedPaymentProduct) return
      if (bigFromFee(selectedPaymentProduct.feePrice).eq(0)) return

      return formatPrice(selectedPaymentProduct.feePrice, undefined, formatterLanguage)
    }, [selectedPaymentProduct, formatterLanguage])

    const isSubmitDisabled = useMemo(() => {
      // Either submit is already processing or no payment product has been selected
      if (isSubmitProcessing) return true
      if (!selectedPaymentProduct) return true

      // Case of stripe not being used
      if (!isStripeUsed) return false

      // Stripe is being used
      if (selectedSavedPaymentMethod === undefined) return true
      if (!isReceiptEmailValid) return true
      if (selectedSavedPaymentMethod === null && !isPaymentElementValid) return true

      return false
    }, [isSubmitProcessing, selectedPaymentProduct, isStripeUsed, selectedSavedPaymentMethod, isReceiptEmailValid, isPaymentElementValid])

    const isPreferredPaymentMethodVisible = useMemo(() => {
      return roles.isClient && paymentProducts.length > 1
    }, [paymentProducts.length, roles.isClient])

    const foreignPurchasePrompt = useMemo(() => {
      switch (productType) {
        case ProductType.VIRTUAL_VISIT:
          return 'foreign_purchase_prompt_matterport'
        default:
          return 'foreign_purchase_prompt'
      }
    }, [productType])

    const handleClose = useCallback(() => {
      onClose?.()
    }, [onClose])

    /** Carries out set beforeConfirmAction */
    const handleSubmit = useCallback(async () => {
      if (!productId || !productQuantity || !beforeConfirmAction) throw new Error('Purchase metadata are not set')
      if (isStripeUsed && !paymentIntent) throw new Error('Payment intent has not been fetched')
      if (!selectedPaymentProduct) throw new Error('Billing product not set')

      if (
        roles.isClient
        && isForeignPurchase
        && !(await showConfirm(t(foreignPurchasePrompt),
          {
            title: t('foreign_purchase_reminder'),
            confirmDenyText: t('foreign_purchase_deny'),
            confirmAcceptText: t('foreign_purchase_confirm')
          }))
      ) return

      if (beforeConfirmActionState !== APIRequestState.OK) {
        beforeConfirmAction(paymentIntent?.id, selectedPaymentProduct.productCode, productId, productQuantity)
      }
      setIsStripeConfirmed(false)
    }, [roles, isForeignPurchase, showConfirm, t, beforeConfirmAction, beforeConfirmActionState, isStripeUsed, paymentIntent, productId, productQuantity, selectedPaymentProduct, setIsStripeConfirmed, foreignPurchasePrompt])

    /** Purges stored billing configuration, resets payment method and carries out afterClosed action if set */
    const resetPurchase = useCallback(() => {
      updatePreferredPaymentMethod.reset()
      queryClient.resetQueries({ queryKey: [EntityKeys.UPSELL_CONFIG, QueryType.GET] })
      setSelectedPaymentProduct(undefined)
      setIsStripeConfirmed(false)
      setStripeError(null)
      if (updatePreferredPaymentMethod.isSuccess) queryClient.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })

      if (!!afterClosed) afterClosed(stripeError, beforeConfirmActionState)
    }, [afterClosed, beforeConfirmActionState, setIsStripeConfirmed, setSelectedPaymentProduct, setStripeError, stripeError, updatePreferredPaymentMethod])

    useEffect(() => {
      if (beforeConfirmActionState === APIRequestState.OK && ((isStripeUsed && isStripeConfirmed && !stripeError) || !isStripeUsed)) {
        onSuccess?.()

        if (!successNode && !onSuccess) {
          spawnSuccessToast(successTitle || t('success'))
          onClose?.()
        }
      }
       
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [beforeConfirmActionState, isStripeConfirmed, isStripeUsed, stripeError, successNode, successTitle])

    useLayoutEffect(() => {
      setDoStripeRedirect(doStripRedirect ?? true)
       
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doStripRedirect])

    // Data safeguard
    if (!onClose || !total || !totalWithPayment) return <Fragment></Fragment>

    return (
      <Modal
        className={styles.upsellModal}
        modalContentClassName={`${styles.upsellPaymentModalContent} ${isLoading ? styles.isMinified : ''}`}
        isOpen={isOpen}
        onClickOutside={handleClose}
        afterClosed={resetPurchase}
        zIndex={1400}
      >

        {!isPurchaseFinished &&
          <div className={`${styles.contentWrap} ${isLoading ? styles.hidden : ''}`}>

            <div className={styles.header}>

              <h3 className={styles.product}>
                {productQuantity}x {t(`product:p_${productId}`)}
              </h3>

              <h1 className={styles.total}>
                {formatPrice(total)} {extraPrice && <span style={{ fontSize: '2.5rem', opacity: 0.5 }}>+{extraPrice}</span>}
              </h1>

              {!!children &&
                <div className={styles.info}>
                  {children}
                </div>
              }

            </div>

            {!stripeError &&
              <Fragment>

                <div className={styles.payment}>

                  <h3 className={styles.title}>
                    {t('choose_method')}
                  </h3>

                  <div className={styles.paymentProducts}>
                    {paymentProducts.map((product) => {

                      const priceAsBig = bigFromFee(product.feePrice)
                      const methodEnabled = product.kind !== ProductKind.INVOICE_BY_EMAIL || userCanPayByInvoice || priceAsBig.gt(0)

                      return (
                        <CheckCard
                          key={product.kind}
                          className={styles.product}
                          checkbox="circle"
                          isRadio={true}
                          isHeaderClickable={true}
                          checked={product === selectedPaymentProduct}
                          highlighted={product === selectedPaymentProduct}
                          disabled={(product !== selectedPaymentProduct && beforeConfirmActionState === APIRequestState.OK) || !methodEnabled}
                          onCheck={() => setSelectedPaymentProduct(product)
                          }
                        >
                          <div className={styles.container}>

                            <div className={styles.left}>

                              <span className={styles.name}>
                                {t(`product_kind:${product.kind}`)}
                              </span>

                              {i18n.exists(`product_kind_disclaimer:${product.kind}`) &&
                                <span className={styles.disclaimer}>
                                  {t(`product_kind_disclaimer:${product.kind}`)}
                                </span>
                              }

                            </div>

                            {isPreferredPaymentMethodVisible && productKindIsPreferredPaymentMethod(product.kind) && methodEnabled &&
                              <div className={styles.right}>

                                {product.kind === ProductKind.INVOICE_BY_EMAIL && priceAsBig.gt(0) &&
                                  <MUIBadge
                                    color="coral"
                                    icon={<CreditCardOutlined fontSize="large" />}
                                    label={t('badges:free_for_subscribed')}
                                  />
                                }

                                <PreferredPaymentMethodSwitch
                                  paymentMethod={product.kind}
                                  directlyUpdateUserMe={false}
                                />

                                {priceAsBig.gt(0) &&
                                  <Typography variant="text-md" fontWeight="bold" flex="0 0 auto">
                                    + {formatPrice(product.feePrice)}
                                  </Typography>
                                }

                              </div>
                            }

                            {!methodEnabled &&
                              <Tag color="red" materialIcon={<LockOutlinedIcon />}>
                                <span>{t('preferred_payment_method:badge_only_subscribed')}</span>
                              </Tag>
                            }

                          </div>
                        </CheckCard>
                      )
                    })}
                  </div>

                  {isStripeUsed &&
                    <Fragment>

                      {/* Payment element */}
                      {!!paymentIntent &&
                        <div className={styles.paymentWrapper}>
                          <StripePaymentWrapper />
                        </div>
                      }

                      {/* Loading for payment element */}
                      {(!paymentIntent || isPaymentLoading) &&
                        <ReactLoading color={Color.GRAY_TEXT} className={styles.loading} type="cylon" />
                      }

                    </Fragment>
                  }

                </div>

              </Fragment>
            }

            {/* Before confirm success if payment fails */}
            <TransitionAppear visible={beforeConfirmActionState === APIRequestState.OK && !!stripeError}>
              {beforeConfirmActionState === APIRequestState.OK && !!stripeError &&
                <BlockInfo className={styles.successWithError} color="green">
                  <h4>{successTitle || t('success')}</h4>
                </BlockInfo>
              }
            </TransitionAppear>

            {/* Stripe error */}
            <TransitionAppear visible={!!stripeError}>
              {!!stripeError &&
                <div className={styles.errorBox}>
                  <BlockInfo color="red">
                    <h4>{t('payment_error')}</h4>
                    <p>{t('stripe_error_text_no_retry')}</p>
                  </BlockInfo>
                </div>
              }
            </TransitionAppear>

            {/* Request error from beforeConfirmAction */}
            <TransitionAppear visible={!!beforeConfirmActionMutation?.isError}>
              {beforeConfirmActionMutation?.isError &&
                <QueryStatus
                  query={beforeConfirmActionMutation}
                  showStates={['error']}
                  spaceBottomRem={2}
                />
              }
            </TransitionAppear>

            <div className={styles.actions}>

              <Button type="secondary" onClick={handleClose}>
                {t('cancel')}
              </Button>

              {!stripeError &&
                <Button onClick={handleSubmit} disabled={isSubmitDisabled}>
                  {t('submit', { fee: formatPrice(totalWithPayment) })}
                </Button>
              }

            </div>

          </div>
        }

        {/* Success screen for orders paid by invoice. Uses either provided JSX Element or defaulted with an optional custom title */}
        {isPurchaseFinished &&
          successNode
        }

        {/* Component-wide global loading with state descriptors */}
        {isLoading &&
          <div className={styles.loadingBox}>
            <ReactLoading color={Color.GRAY_TEXT} className={styles.loading} type="cylon" />

            {!isSubmitProcessing && <p className={styles.loadingMessage}>{t('loading_states.setup')}</p>}
            {beforeConfirmActionState === APIRequestState.RUNNING && <p className={styles.loadingMessage}>{t('loading_states.action')}</p>}
            {isStripeProcessing && <p className={styles.loadingMessage}>{t('loading_states.stripe')}</p>}
          </div>
        }
      </Modal>
    )
  }
